'use client';
import { StreamStepperFlow } from '@components/FormComponents/StreamStepperFlow/StreamStepperFlow';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { DeletionModal } from '@components/DeletionModal/DeletionModal';
import { renderActionIcon } from '../utils/RenderActionIcon';
import { EditModal } from '@components/EditModal/EditModal';
import { DeleteButton, EditButton } from '../TableActions';
import { deleteStream, updateStream } from '@utils/graphql/creators/streams';
import { getLogger } from '@utils/general';
import { MenuItem, Divider } from '@mui/material';
import { useRef } from 'react';
import '../TableActions.css';
import { handleTableActionsKeyDown } from '../utils/helpers';

const logger = getLogger('src.pages.split.streams.StreamActions');

export const StreamActions = ({
  rowInfo, id, userIsAdmin,
}) => {
  const streamIsOnline = rowInfo.online;
  const [title, setTitle] = useState('');

  const handleOnlineOffline = async (streamId, streamName) => {
    const offlineOrOnline = streamIsOnline ? 'offline' : 'online';
    const streamData = { online: !streamIsOnline };
    const { execute } = updateStream('update_stream', streamId, streamData);

    const failMessage = `Failed to set stream ${streamName} ${offlineOrOnline}.`;

    try {
      await execute()
        .then(({ error }) => {
          if (error) {
            logger.error(error, failMessage);
          } else {
            logger.info(`Successfully set stream ${streamName} ${offlineOrOnline}.`);
          }
        });
    }
    catch (error) {
      logger.error(error, failMessage);
    }
  };

  const onOffButtonRef = useRef(null);
  const editButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);
  const buttonArray = [
    onOffButtonRef,
    editButtonRef,
    deleteButtonRef,
  ];

  return [
    <MenuItem
      key={`onlineOffline-${rowInfo.id}`}
      onKeyDown={(event) => {
        handleTableActionsKeyDown(event, buttonArray, 0);
      }}
    >
      <button
        ref={onOffButtonRef}
        className='actions-container'
        tabIndex={-1}
        onClick={() => handleOnlineOffline(rowInfo.id, rowInfo.name)}
      >
        <i
          aria-hidden='true'
          className={renderActionIcon('onlineOffline', streamIsOnline)}
        />
        {streamIsOnline ? 'Set Offline' : 'Set Online'}
      </button>
    </MenuItem>,

    ...(
      userIsAdmin ? [
        <Divider key='divider-1' />,
        <MenuItem
          key='edit-stream-button'
          onKeyDown={(event) => {
            handleTableActionsKeyDown(event, buttonArray, 1);
          }}>
          <EditModal
            data={rowInfo.id}
            FormComponent={StreamStepperFlow}
            setTitle={setTitle}
            title={title}
            fieldName='stream'
            trigger={({ handleOpen }) =>
              <EditButton
                ref={editButtonRef}
                focusIdOnEscape={id}
                handleOpen={handleOpen}
                data={rowInfo}
              />
            }
          />
        </MenuItem>,
        <Divider key='divider-2' />,
        <MenuItem
          key='delete-stream-button'
          onKeyDown={(event) => {
            handleTableActionsKeyDown(event, buttonArray, 2);
          }}>
          <DeletionModal
            data={rowInfo}
            handleDelete={deleteStream}
            fieldName='stream'
            trigger={({ handleOpen }) =>
              <DeleteButton
                ref={deleteButtonRef}
                focusIdOnEscape={id}
                handleOpen={handleOpen}
                data={rowInfo}
              />
            }
          />
        </MenuItem>,
      ] : []
    ),
  ];
};

StreamActions.propTypes = {
  rowInfo: PropTypes.object,
  setTitle: PropTypes.func,
  firstButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
};